import {
  useState,
  useEffect,
  createContext,
  useContext,
  isValidElement,
  cloneElement,
  Children,
  type CSSProperties,
  type ReactNode,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import {
  Button as PennyButton,
  NakedButton as PennyNakedButton,
  Pill as PennyPill,
  Avatar as PennyAvatar,
  Text as PennyText,
  TextField as PennyTextField,
  FormField as PennyFormField,
  SelectNew,
  Icon as PennyIcon,
  IconButton as PennyIconButton,
  Illustration as PennyIllustration,
  SectionBannerRoot,
  SectionBannerContent,
  SectionBannerDescription,
  type IconKey,
  type IconProps as PennyIconProps,
  type IllustrationProps as PennyIllustrationProps,
  type TextProps,
  type TextStyleKey,
} from '@melio/penny';

import { asset } from '@/lib/assets';
import type { AppContextValue, SelectOption } from '@/lib/types';

// toLabel — flatten a ReactNode into a plain string for penny's `label` prop.
// Returns the string/number directly, joins string descendants of elements, else ''.
function toLabel(node: ReactNode): string {
  if (node == null || node === false || node === true) return '';
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(toLabel).join('');
  if (isValidElement(node)) {
    const childProps = (node.props ?? {}) as { children?: ReactNode };
    return Children.toArray(childProps.children).map(toLabel).join('');
  }
  return '';
}

// ds.tsx — shared Melio/Penny primitives + dark code block for the developer portal.

// ---- Icon ---------------------------------------------------------------
// Backed by @melio/penny's <Icon>, which ships the glyphs itself (no runtime fetch) and types
// `type` as IconKey, so a misspelled name is a compile error rather than a silently blank span.
//
// Penny's public icon scale is 12 / 16 / 32px. Our call sites ask for arbitrary pixel sizes, so
// `size` snaps to the nearest step on that scale — the portal ends up on the design system's
// scale rather than beside it.
type PennyIconSize = NonNullable<PennyIconProps['size']>;
function toIconSize(size: number): PennyIconSize {
  if (size <= 14) return 'extra-small'; // 12px
  if (size <= 24) return 'small'; // 16px
  return 'large'; // 32px
}

interface IconProps {
  name: IconKey;
  size?: number;
  color?: string;
  style?: CSSProperties;
}
// `color` stays a free-form CSS value (call sites pass tokens, computed values and decorative
// per-entity colors). Penny's own `color` enum covers only seven semantic roles, so we hand it
// 'inherit' and set the CSS color on the wrapper — every existing color is preserved exactly.
function Icon({ name, size = 20, color, style }: IconProps) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: color || 'currentColor',
        flexShrink: 0,
        lineHeight: 0,
        ...style,
      }}
    >
      <PennyIcon type={name} size={toIconSize(size)} color="inherit" />
    </span>
  );
}

// ---- Illustration -------------------------------------------------------
// Backed by @melio/penny's <Illustration>. Penny's scale is small/medium/large = 96/120/144px.
type PennyIllustrationType = PennyIllustrationProps['type'];
interface IllustrationProps {
  name: PennyIllustrationType;
  size?: number;
  style?: CSSProperties;
}
function Illustration({ name, size = 120, style }: IllustrationProps) {
  const pennySize: NonNullable<PennyIllustrationProps['size']> = size <= 108 ? 'small' : size <= 132 ? 'medium' : 'large';
  return (
    <span style={{ display: 'inline-flex', ...style }}>
      <PennyIllustration type={name} size={pennySize} />
    </span>
  );
}

interface MelioLogoProps {
  height?: number;
  inverse?: boolean;
  style?: CSSProperties;
}
function MelioLogo({ height = 22, inverse = false, style }: MelioLogoProps) {
  return (
    <img
      src={asset(inverse ? 'melio-wordmark-inverse.svg' : 'melio-wordmark.svg')}
      alt="Melio"
      style={{ height, display: 'block', ...style }}
    />
  );
}

// ---- Mono ---------------------------------------------------------------
// Monospace code/identifier text. Penny's type scale has no monospace style — the portal shows a
// lot of keys, ids and endpoint paths — so this is the one place the font family is overridden,
// on top of a penny <Text> so size, color and truncation still come from the design system.
type MonoSize = 'caption' | 'body' | 'large';
const MONO_TEXT_STYLE: Record<MonoSize, TextStyleKey> = {
  caption: 'caption1',
  body: 'body4',
  large: 'body3Semi',
};
interface MonoProps {
  children?: ReactNode;
  size?: MonoSize;
  color?: TextProps['color'];
  bold?: boolean;
  style?: CSSProperties;
}
function Mono({ children, size = 'body', color, bold, style }: MonoProps) {
  return (
    <PennyText
      textStyle={MONO_TEXT_STYLE[size]}
      color={color}
      style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: bold ? 600 : undefined, ...style }}
    >
      {children}
    </PennyText>
  );
}

// ---- Global app context (accent, env, partner type, language, code theme) ----
const AppCtx = createContext<AppContextValue | null>(null);
const useApp = (): AppContextValue => {
  const ctx = useContext(AppCtx);
  if (!ctx) throw new Error('useApp must be used within an AppProvider');
  return ctx;
};

// ---- Buttons ------------------------------------------------------------
type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'invert' | 'critical';
type ButtonSize = 'small' | 'medium' | 'large';
interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children?: ReactNode;
  leftIcon?: IconKey;
  rightIcon?: IconKey;
  disabled?: boolean;
  /** Penny renders its own spinner and locks the button; prefer this over a child <Spinner>. */
  loading?: boolean;
  fullWidth?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  style?: CSSProperties;
  accent?: string;
}
// penny-backed: maps our props onto <Button> from @melio/penny.
const BUTTON_VARIANT_MAP: Record<ButtonVariant, 'primary' | 'secondary' | 'tertiary' | 'primary-inverse' | 'critical'> = {
  primary: 'primary',
  secondary: 'secondary',
  tertiary: 'tertiary',
  ghost: 'tertiary',
  invert: 'primary-inverse',
  critical: 'critical',
};
// Penny's variants diverge from the original in three ways we fix here:
// - tertiary: uses interactive-rest border (darker) vs original's border-static (lighter)
// - ghost:    tertiary-based but needs transparent bg + no border
// - invert:   primary-inverse-based but uses brand purple text vs original's neutral-1000
const BUTTON_VARIANT_STYLE: Partial<Record<ButtonVariant, CSSProperties>> = {
  tertiary: { borderColor: 'var(--border-static)' },
  ghost: { backgroundColor: 'transparent', border: 'none' },
  invert: { color: 'var(--neutral-1000)' },
};
function Button({
  variant = 'primary',
  size = 'medium',
  children,
  leftIcon,
  rightIcon,
  disabled,
  loading,
  fullWidth,
  onClick,
  type = 'button',
  style,
}: ButtonProps) {
  const iconSize = size === 'large' ? 20 : 18;
  return (
    <PennyButton
      variant={BUTTON_VARIANT_MAP[variant]}
      size={size}
      label={toLabel(children)}
      leftElement={leftIcon ? <Icon name={leftIcon} size={iconSize} /> : undefined}
      rightElement={rightIcon ? <Icon name={rightIcon} size={iconSize} /> : undefined}
      isDisabled={disabled}
      isLoading={loading}
      isFullWidth={fullWidth}
      onClick={onClick}
      // minWidth: auto overrides penny's 80/96/112px minimum; variant styles fix ghost/invert
      style={{ minWidth: 'auto', ...BUTTON_VARIANT_STYLE[variant], ...style }}
      // penny's Button types omit the native `type` attr; forward it via rest props.
      {...({ type } as { type?: 'button' | 'submit' | 'reset' })}
    />
  );
}

interface NakedButtonProps {
  children?: ReactNode;
  leftIcon?: IconKey;
  rightIcon?: IconKey;
  onClick?: (e?: ReactMouseEvent) => void;
  color?: string;
  disabled?: boolean;
  style?: CSSProperties;
  size?: number;
}
// penny-backed: maps our props onto <NakedButton> from @melio/penny.
function NakedButton({ children, leftIcon, rightIcon, onClick, disabled, style }: NakedButtonProps) {
  return (
    <PennyNakedButton
      label={toLabel(children)}
      variant="primary"
      size="medium"
      leftElement={leftIcon ? <Icon name={leftIcon} size={16} /> : undefined}
      rightElement={rightIcon ? <Icon name={rightIcon} size={16} /> : undefined}
      onClick={onClick}
      isDisabled={disabled}
      style={style}
    />
  );
}

interface IconButtonProps {
  name: IconKey;
  onClick?: () => void;
  size?: number;
  iconSize?: number;
  ariaLabel?: string;
  /** Penny's naked variant is transparent-with-hover; 'tertiary' gives the bordered treatment. */
  variant?: 'naked' | 'naked-inverse' | 'tertiary' | 'critical';
  style?: CSSProperties;
}
// penny-backed: <IconButton> from @melio/penny. Penny owns the hover/focus/disabled states that
// the previous version hand-rolled with a `useState` hover flag, so `hoverBg` and `color` are gone
// — the variant decides both. Numeric `size` snaps to penny's button scale.
function toIconButtonSize(size: number): 'extra-small' | 'small' | 'medium' | 'large' {
  if (size <= 24) return 'extra-small';
  if (size <= 32) return 'small';
  if (size <= 40) return 'medium';
  return 'large';
}
function IconButton({ name, onClick, size = 36, ariaLabel, variant = 'naked', style }: IconButtonProps) {
  return (
    <PennyIconButton icon={name} size={toIconButtonSize(size)} variant={variant} onClick={onClick} aria-label={ariaLabel} style={style} />
  );
}

// ---- Status pill --------------------------------------------------------
type PillKind = 'success' | 'informative' | 'warning' | 'critical' | 'brand' | 'neutral';
interface PillProps {
  kind?: PillKind;
  children?: ReactNode;
  style?: CSSProperties;
}
// penny-backed: maps our props onto <Pill> from @melio/penny.
// Pill has no `style` prop, so wrap in a <span> when one is supplied.
function Pill({ kind = 'neutral', children = true, style }: PillProps) {
  const pill = <PennyPill status={kind} label={toLabel(children)} />;
  return style ? <span style={style}>{pill}</span> : pill;
}

// ---- Card ---------------------------------------------------------------
interface CardProps {
  children?: ReactNode;
  padding?: number;
  style?: CSSProperties;
  onClick?: () => void;
  hoverable?: boolean;
}
// Penny's Card uses a ::before pseudo-element for its border which isn't overridable via
// inline styles — cards with `border: 'none'` or custom borderColor break. Use a plain div
// (same as the original ds.jsx) to faithfully match the original design.
function Card({ children, padding = 24, style, onClick, hoverable }: CardProps) {
  const [h, setH] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        background: 'var(--surface-primary)',
        border: '1px solid var(--border-static)',
        borderRadius: 'var(--radius-card)',
        padding,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color .18s, box-shadow .18s',
        ...((onClick || hoverable) && h ? { borderColor: 'var(--neutral-500)', boxShadow: 'var(--shadow-400)' } : {}),
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ---- Form bits ----------------------------------------------------------
// penny-backed: <FormField> + <TextField> / <SelectNew>. Penny owns the label/helper/error
// wiring (including the aria-describedby and aria-invalid plumbing the hand-rolled versions
// never emitted), the focus ring and the disabled/read-only treatments.
//
// These three exist so the non-react-hook-form screens — the webhook drawer, the log filter, the
// env pickers — can use a plain value/onChange pair. Screens built on `useMelioForm` (onboarding,
// welcome) should use penny's <Form>/<FormField> directly rather than reaching for these.
interface FieldProps {
  label?: ReactNode;
  helper?: ReactNode;
  error?: ReactNode;
  children?: ReactNode;
  required?: boolean;
  style?: CSSProperties;
}
function Field({ label, helper, error, children, required, style }: FieldProps) {
  return (
    <div style={style}>
      <PennyFormField
        isRequired={required}
        labelProps={label ? { label: toLabel(label) } : undefined}
        helperText={helper ?? undefined}
        error={error ? { message: toLabel(error) } : undefined}
        // Penny generates the field id and the invalid/required state and hands them back through
        // `render`; cloning the child puts them on the control itself, which is what the label and
        // the helper/error text need to point at.
        render={(props) => (isValidElement(children) ? cloneElement(children, props) : <span {...props}>{children}</span>)}
      />
    </div>
  );
}

interface InputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  error?: boolean;
  disabled?: boolean;
  prefix?: ReactNode;
  suffix?: ReactNode;
  type?: string;
  mono?: boolean;
  fontSize?: number;
  style?: CSSProperties;
}
function Input({ value, onChange, placeholder, error, disabled, prefix, suffix, type = 'text', mono, style, ...rest }: InputProps) {
  return (
    <PennyTextField
      {...rest}
      value={value ?? ''}
      onChange={(e) => onChange?.(e.target.value)}
      placeholder={placeholder}
      isInvalid={error}
      isDisabled={disabled}
      type={type}
      leftElement={prefix ? <>{prefix}</> : undefined}
      rightElement={suffix ? <>{suffix}</> : undefined}
      // The portal shows endpoints, keys and ids in a few of these; penny has no monospace variant.
      style={mono ? { fontFamily: "'JetBrains Mono', monospace", ...style } : style}
    />
  );
}

interface SelectProps {
  value?: string;
  onChange?: (value: string) => void;
  options: Array<SelectOption | string>;
  style?: CSSProperties;
  ariaLabel?: string;
}
function Select({ value, onChange, options, style, ariaLabel }: SelectProps) {
  const normalized = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  return (
    <div style={style}>
      <SelectNew
        options={normalized}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        aria-label={ariaLabel}
        shouldHideClearButton
      />
    </div>
  );
}

// ---- Banner -------------------------------------------------------------
type BannerKind = 'informative' | 'success' | 'warning' | 'critical' | 'brand' | 'neutral';
interface BannerProps {
  kind?: BannerKind;
  children?: ReactNode;
  cta?: ReactNode;
  onCta?: () => void;
  icon?: string;
  style?: CSSProperties;
}
// penny-backed: <SectionBanner*> compound from @melio/penny. Our `kind` names all map
// 1:1 to penny's SectionBannerVariant. `cta`/`onCta` render as a trailing NakedButton
// inside the content. SectionBanner has no `style` prop, so wrap in a styled <div>.
// `icon` is ignored (penny picks the icon from the variant).
function Banner({ kind = 'informative', children, cta, onCta, style }: BannerProps) {
  const banner = (
    <SectionBannerRoot variant={kind}>
      <SectionBannerContent>
        <SectionBannerDescription>{children}</SectionBannerDescription>
        {cta && <NakedButton onClick={onCta}>{cta}</NakedButton>}
      </SectionBannerContent>
    </SectionBannerRoot>
  );
  return style ? <div style={style}>{banner}</div> : banner;
}

// ---- Avatar -------------------------------------------------------------
interface AvatarProps {
  initials?: ReactNode;
  color?: string;
  size?: number;
  style?: CSSProperties;
}
// penny-backed: <Avatar> from @melio/penny. Numeric `size` maps to the nearest penny
// enum; `color` is ignored (penny picks a default bgColor). Avatar has no `style` prop,
// so wrap in a <span> when one is supplied.
function Avatar({ initials, size = 32, style }: AvatarProps) {
  const pennySize: 'small' | 'medium' | 'large' = size <= 20 ? 'small' : size <= 34 ? 'medium' : 'large';
  const avatar = <PennyAvatar name={toLabel(initials)} size={pennySize} />;
  return style ? <span style={style}>{avatar}</span> : avatar;
}

export {
  Icon,
  Illustration,
  MelioLogo,
  Mono,
  AppCtx,
  useApp,
  Button,
  NakedButton,
  IconButton,
  Pill,
  Card,
  Field,
  Input,
  Select,
  Banner,
  Avatar,
};
// Re-exported so call sites can type their own icon-name props/data without reaching past `ds`.
export type { IconKey };
